import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { trainingBlockApi } from '../../api'
import type { TrainingTagDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES, flattenTrainingTags } from '../../api/trainingBlockApi'
import type { ApiError } from '../../api/client'
import type { TrainingBlock } from '../../types/api'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf, isCoachedTrainee } from '../../utils/entitlements'
import { Button, ExperimentalTextImportOption } from '../ui'
import SoloLibraryLocked from './SoloLibraryLocked'
import { TrainingBlockCard } from './TrainingBlockCard'
import { TagCategoryFilter } from './TagCategoryFilter'
import {
  emptyTagCategoryFilterState,
  matchesTagCategoryFilters,
  type TagCategoryFilterState,
} from './tagCategoryFilterUtils'

export default function TrainingBlocks() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const canCreate = entitlementsOf(user).createOwnBlocksAndPrograms
  const blocked = isCoachedTrainee(user)

  const [blocks, setBlocks] = useState<TrainingBlock[]>([])
  const [availableTags, setAvailableTags] = useState<TrainingTagDto[]>([])
  const [activeFilters, setActiveFilters] = useState<TagCategoryFilterState>(emptyTagCategoryFilterState())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadBlocks = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await trainingBlockApi.getBlocks()
      setBlocks(data)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.trainingBlocks.error.loadFailed)
    } finally {
      setLoading(false)
    }
  }, [t.trainingBlocks.error.loadFailed])

  useEffect(() => {
    if (blocked) return
    void loadBlocks()
    trainingBlockApi.getTags().then(g => setAvailableTags(flattenTrainingTags(g))).catch(() => {})
  }, [blocked, loadBlocks])

  async function handleCopy(id: number, name: string) {
    if (!confirm(t.trainingBlocks.error.copyConfirm.replace('{{name}}', name))) return
    try {
      const copy = await trainingBlockApi.copyBlock(id)
      setBlocks(prev => [copy, ...prev])
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingBlocks.error.copyFailed)
    }
  }

  async function handleDelete(id: number, name: string) {
    try {
      const usage = await trainingBlockApi.getBlockUsage(id)
      const programNames = usage.programs.map(program => program.name)
      const message = programNames.length > 0
        ? t.trainingBlocks.error.deleteConfirmInUse
            .replace('{{name}}', name)
            .replace('{{programs}}', programNames.join(', '))
        : t.trainingBlocks.error.deleteConfirm.replace('{{name}}', name)
      if (!confirm(message)) return
      await trainingBlockApi.deleteBlock(id)
      setBlocks(prev => prev.filter(b => b.id !== id))
    } catch (err) {
      const apiError = err as ApiError
      alert(apiError.message || t.trainingBlocks.error.deleteFailed)
    }
  }

  const tagEmojiMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.emoji])),
    [availableTags]
  )

  const tagLabelMap = useMemo(
    () => Object.fromEntries(availableTags.map(tag => [tag.key, tag.label])),
    [availableTags]
  )

  const filteredBlocks = useMemo(() => {
    return blocks.filter(b => matchesTagCategoryFilters(b.tags, activeFilters))
  }, [blocks, activeFilters])

  const usedTagKeys = useMemo(() => {
    const keys = new Set<string>()
    blocks.forEach(b => b.tags?.forEach(tag => keys.add(tag)))
    return keys
  }, [blocks])

  if (blocked) {
    return <SoloLibraryLocked pageTitle={t.trainingBlocks.title} />
  }

  return (
    <div className="p-5">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.trainingBlocks.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>{t.trainingBlocks.subtitle}</p>
      </header>

      {canCreate ? (
        <>
          <Button
            type="button"
            variant="primary"
            size="md"
            fullWidth
            className="mb-3"
            onClick={() => navigate('/training-blocks/create')}
          >
            {t.trainingBlocks.createBlock}
          </Button>

          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px" style={{ backgroundColor: theme.cardBorder }} />
            <span className="text-xs" style={{ color: theme.text.muted }}>{t.createTrainingBlock.orDivider}</span>
            <div className="flex-1 h-px" style={{ backgroundColor: theme.cardBorder }} />
          </div>

          <ExperimentalTextImportOption
            className="mb-6"
            title={t.trainingBlocks.createInBot}
            description={t.trainingBlocks.createInBotHint}
            onClick={() => navigate('/training-blocks/create-from-text')}
          />
        </>
      ) : (
        <p className="text-sm mb-6" style={{ color: theme.text.muted }}>
          {t.subscription.locked.createOwn}
        </p>
      )}

      <TagCategoryFilter
        className="mb-5"
        tags={availableTags}
        usedTagKeys={usedTagKeys}
        categories={BLOCK_TAG_CATEGORIES}
        value={activeFilters}
        onChange={setActiveFilters}
      />

      {error && (
        <div className="rounded-xl p-4 mb-4 bg-red-500/10 border border-red-500/25">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10" style={{ color: theme.text.muted }}>{t.trainingBlocks.loading}</div>
      ) : filteredBlocks.length === 0 ? (
        <div className="flex flex-col items-center py-14 text-center">
          <span className="text-5xl mb-4">{blocks.length === 0 ? '🏋️' : '🔍'}</span>
          <p className="font-semibold mb-1" style={{ color: theme.text.primary }}>
            {blocks.length === 0 ? t.trainingBlocks.emptyState.title : t.trainingBlocks.filterEmpty}
          </p>
          {blocks.length === 0 && (
            <p className="text-sm px-8" style={{ color: theme.text.muted }}>
              {t.trainingBlocks.emptyState.description}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredBlocks.map(block => (
            <TrainingBlockCard
              key={block.id}
              name={block.name}
              items={block.items}
              tags={block.tags}
              tagEmojiMap={tagEmojiMap}
              tagLabelMap={tagLabelMap}
              onClick={() => navigate(`/training-blocks/${block.id}/edit`)}
              footer={
                <>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      title={t.trainingBlocks.editBlock}
                      onClick={() => navigate(`/training-blocks/${block.id}/edit`)}
                      className="p-3 rounded-xl transition-colors"
                      style={{ color: theme.accent.text }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    {canCreate && <button
                      type="button"
                      title={t.trainingBlocks.copyBlock}
                      onClick={() => handleCopy(block.id, block.name)}
                      className="p-3 rounded-xl transition-opacity"
                      style={{ color: theme.text.muted }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    </button>}
                  </div>
                  <button
                    type="button"
                    title={t.trainingBlocks.deleteBlock}
                    onClick={() => handleDelete(block.id, block.name)}
                    className="p-3 rounded-xl transition-colors"
                    style={{ color: '#ef4444' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6"/><path d="M14 11v6"/>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </>
              }
            />
          ))}
        </div>
      )}

    </div>
  )
}
